import { Fragment, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Building2, Check, Wrench } from "lucide-react";
import { useEmpresaAtiva } from "../hooks/useEmpresaAtiva";
import { useAuth } from "../hooks/useAuth";
import { lembrarEmpresa } from "../lib/empresaDaAba";
import { cn } from "../lib/utils";
import { camadaDaRota } from "../lib/operador";
import { agruparEmpresas } from "../lib/grupoDaEmpresa";

const GOLD = "#C9A55C";

/**
 * Switcher de contexto — indica E troca onde você está.
 * Para o dono da plataforma (is_owner) há a opção "Operador" no topo: uma camada
 * ACIMA dos clientes (M5/Regula/futuros). Sair de um cliente pro Operador (e
 * vice-versa) é por aqui — o Operador não é mais um item do menu do M5.
 * Lembra o último contexto em localStorage ('mrg-home') pro login cair certo.
 *
 * As empresas saem em DOIS GRUPOS NOMEADOS ("Obras & Facilities" e "Auditoria &
 * Conformidade"), não numa lista corrida sob "CLIENTES". Motivo: os dois
 * softwares da holding não se misturam — nem dado, nem jargão, nem marca — e
 * quem abre este menu não conhece a arquitetura pra deduzir isso dos nomes.
 * A VORTEK continua FORA dos grupos, no topo: ela é a holding, não uma empresa
 * da lista. O grupo vem de `grupoDaEmpresa` (por produto), NÃO de
 * `marcaDaEmpresa` (por pele) — ver o comentário lá, o Átomo separa as duas.
 */
export function EmpresaSwitcher() {
  const { empresas, ativa, trocar, loading } = useEmpresaAtiva();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isOwner = !!user?.is_owner;
  // MESMA fonte do Layout (`camadaDaRota`): o switcher e a sidebar não podem
  // dizer coisas diferentes sobre em que camada a aba está. Até o #1436 o Layout
  // exigia `&& is_owner` aqui e o switcher não — então um admin não-owner em
  // /operador/* lia "VORTEK" no switcher e o logo do cliente na sidebar.
  const onOperador = camadaDaRota(location.pathname) === "plataforma";

  // Aparece pra quem tem >1 empresa OU pro dono (que tem o Operador).
  if (loading || !ativa) return null;
  if (empresas.length < 2 && !isOwner) return null;

  function irOperador() {
    try { localStorage.setItem("mrg-home", "operador"); } catch { /* ignore */ }
    setOpen(false);
    navigate("/operador");
  }

  function irEmpresa(id: string) {
    try { localStorage.setItem("mrg-home", "empresa"); } catch { /* ignore */ }
    setOpen(false);
    if (onOperador) {
      // saindo do Operador pra dentro de um cliente: vai pro painel dele
      //
      // 🔴 Aqui gravava SÓ a empresa. O slug da pele ficava o da empresa
      // anterior, o boot escolhia a pele errada e o `useEmpresaAtiva` corrigia
      // com um `window.location.reload()` — um boot inteiro jogado fora. Medido
      // em produção 28/07: 3 chamadas a /me/perfil e 2 a /me/empresas pra UM
      // clique, com tudo "pendente" no meio do caminho. `lembrarEmpresa` grava
      // as três coisas juntas.
      lembrarEmpresa(id, empresas.find((e) => e.id === id)?.slug);
      window.location.assign("/");
    } else {
      trocar(id); // troca entre clientes (recarrega)
    }
  }

  // Só os grupos que TÊM empresa — quem acessa um lado só não pode ler o título
  // do outro com nada embaixo e achar que perdeu acesso.
  const grupos = agruparEmpresas(empresas);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-colors",
          onOperador
            ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-accent-ink dark:text-accent-ink"
            : "bg-background hover:bg-accent"
        )}
        aria-label="Trocar contexto (cliente ou Operador)"
      >
        {onOperador ? (
          <Wrench className="h-4 w-4" style={{ color: GOLD }} />
        ) : (
          <Building2 className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium">{onOperador ? "VORTEK" : ativa.nome}</span>
        <ChevronDown className={cn("h-4 w-4 opacity-60 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 bg-popover border rounded-md shadow-lg z-50 py-1">
          {isOwner && (
            <>
              <button
                onClick={irOperador}
                disabled={onOperador}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                  onOperador && "cursor-default"
                )}
              >
                <Wrench className="h-4 w-4" style={{ color: GOLD }} />
                <span className="flex-1 font-medium" style={{ color: GOLD }}>VORTEK · Operador</span>
                {onOperador && <Check className="h-4 w-4" style={{ color: GOLD }} />}
              </button>
              <div className="my-1 border-t" />
            </>
          )}
          {grupos.map((g, i) => (
            <Fragment key={g.chave}>
              {/* Separador só ENTRE grupos: acima do primeiro já veio o do bloco
                  VORTEK (quando é dono), e sem dono não há o que separar. */}
              {i > 0 && <div className="my-1 border-t" />}
              <div className="px-3 pb-1 text-[10px] text-muted-foreground uppercase tracking-wider">
                {g.titulo}
              </div>
              {g.itens.map((e) => {
                const active = !onOperador && e.id === ativa.id;
                return (
                  <button
                    key={e.id}
                    onClick={() => irEmpresa(e.id)}
                    disabled={active}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                      active && "cursor-default opacity-100"
                    )}
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 font-medium">{e.nome}</span>
                    {active && <Check className="h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </Fragment>
          ))}
          <div className="px-3 py-1.5 mt-1 border-t text-[10px] text-muted-foreground uppercase tracking-wider">
            Trocar de cliente recarrega o painel
          </div>
        </div>
      )}
    </div>
  );
}
